import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

// Hero rotating images
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1758957701419-2c6e266f7988?w=1200&q=80",
  "https://images.unsplash.com/photo-1615876234886-fd9a39fda97f?w=1200&q=80",
  "https://images.unsplash.com/photo-1642455680602-7b4ddf7560a4?w=1200&q=80",
  "https://images.unsplash.com/photo-1768511731493-d3c2aa483bb5?w=1200&q=80"
];

// Single environment image for each poster size
const SMALL_IMAGE = "https://images.unsplash.com/photo-1642260550231-4296e40589b5?w=800&q=80";
const MEDIUM_IMAGE = "https://images.unsplash.com/photo-1615876234886-fd9a39fda97f?w=800&q=80";
const LARGE_IMAGE = "https://images.unsplash.com/photo-1758957701419-2c6e266f7988?w=800&q=80";

// Example goals for each size
const EXAMPLE_GOALS = {
  small: ["Wake up at 6am", "Exercise daily", "Read 30 mins"],
  medium: ["Launch my business", "Save $10,000", "Learn a new skill"],
  large: ["Travel the world", "Write a book", "Build my dream home"],
};

const HomePage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [goals, setGoals] = useState({ goal1: "", goal2: "", goal3: "" });
  const navigate = useNavigate();

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleGoalChange = (field, value) => {
    setGoals(prev => ({ ...prev, [field]: value }));
  };

  const hasAtLeastOneGoal = goals.goal1.trim() || goals.goal2.trim() || goals.goal3.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasAtLeastOneGoal) {
      localStorage.setItem("userGoals", JSON.stringify(goals));
      navigate("/select");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero-section px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-[1800px] mx-auto">
          {/* Nav */}
          <nav className="flex justify-between items-center mb-24 animate-fade-in-up">
            <span 
              data-testid="logo"
              className="font-['Syne'] font-extrabold text-2xl tracking-tight"
            >
              CUE.PO
            </span>
          </nav>

          {/* Main Hero Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            {/* Left: Text + Form */}
            <div className="space-y-12">
              <h1 
                data-testid="hero-heading"
                className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[0.9] animate-fade-in-up"
              >
                DEFINE<br />
                YOUR<br />
                <span className="text-[#002FA7]">GOAL</span>
              </h1>
              
              <p 
                data-testid="hero-subtext"
                className="text-lg md:text-xl text-gray-500 max-w-lg animate-fade-in-up animation-delay-100"
              >
                Transform your ambitions into visual cues that prompt action. Enter your goal and we&apos;ll create a poster that keeps you focused.
              </p>

              {/* Goal Form */}
              <form 
                onSubmit={handleSubmit} 
                className="space-y-6 animate-fade-in-up animation-delay-200"
                data-testid="goal-form"
              >
                <div className="grid grid-cols-3 gap-4">
                  <div className="relative">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 block">Goal 1</label>
                    <input
                      type="text"
                      value={goals.goal1}
                      onChange={(e) => handleGoalChange("goal1", e.target.value)}
                      placeholder="First goal"
                      className="goal-input text-base"
                      data-testid="goal-input-1"
                    />
                  </div>
                  <div className="relative">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 block">Goal 2</label>
                    <input
                      type="text"
                      value={goals.goal2}
                      onChange={(e) => handleGoalChange("goal2", e.target.value)}
                      placeholder="Second goal"
                      className="goal-input text-base"
                      data-testid="goal-input-2"
                    />
                  </div>
                  <div className="relative">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 block">Goal 3</label>
                    <input
                      type="text"
                      value={goals.goal3}
                      onChange={(e) => handleGoalChange("goal3", e.target.value)}
                      placeholder="Third goal"
                      className="goal-input text-base"
                      data-testid="goal-input-3"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="submit-goal-btn"
                  className="btn-primary group flex items-center gap-3 text-base"
                  disabled={!hasAtLeastOneGoal}
                >
                  Choose Your Poster
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            </div>

            {/* Right: Auto-rotating Hero Image */}
            <div className="animate-fade-in-up animation-delay-300">
              <div className="aspect-[4/5] bg-gray-100 overflow-hidden relative">
                {HERO_IMAGES.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Interior design ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      idx === currentImageIndex ? "opacity-100" : "opacity-0"
                    }`}
                    data-testid={`hero-image-${idx}`}
                  />
                ))}
              </div>
              {/* Image indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {HERO_IMAGES.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentImageIndex ? "bg-[#002FA7]" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blue Accent Block */}
      <section className="blue-block py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <h2 
            data-testid="accent-heading"
            className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white leading-tight"
          >
            YOU DEFINE.<br />
            WE DESIGN.<br />
            IT GETS DONE.
          </h2>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {/* Small */}
            <div className="space-y-6" data-testid="preview-small">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={SMALL_IMAGE}
                  alt="Small poster in desk environment"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold">Small</h3>
                <p className="text-gray-500">12×16 inches &mdash; $25</p>
                <p className="text-sm text-gray-400 mt-1">Perfect for desks & shelves</p>
              </div>
            </div>

            {/* Medium */}
            <div className="space-y-6 md:mt-12" data-testid="preview-medium">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={MEDIUM_IMAGE}
                  alt="Medium poster in living room"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold">Medium</h3>
                <p className="text-gray-500">16×20 inches &mdash; $45</p>
                <p className="text-sm text-gray-400 mt-1">Statement piece for living rooms</p>
              </div>
            </div>

            {/* Large */}
            <div className="space-y-6 md:mt-24" data-testid="preview-large">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={LARGE_IMAGE}
                  alt="Large poster in gallery room"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold">Large</h3>
                <p className="text-gray-500">20×30 inches &mdash; $65</p>
                <p className="text-sm text-gray-400 mt-1">Gallery-sized masterpiece</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#F4F4F5]" data-testid="who-we-are-section">
        <div className="max-w-[1800px] mx-auto">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-12">WHO WE ARE</h2>
          <div className="aspect-video max-w-4xl mx-auto bg-black rounded-sm overflow-hidden">
            <iframe
              src="https://www.youtube.com/embed/dQw4w9WgXcQ"
              title="Who We Are Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="who-we-are-video"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
